import { lazy, Suspense, useMemo, useState } from "react";
import type { Trend, TrendSeries } from "@/api/queries";
import { useFormatter, type ValueFormat } from "@/artifacts/format";
import type { Artifact } from "@/artifacts/types";
import { Button } from "@/components/ui/Button";
import { Chip } from "@/components/ui/Chip";
import { EmptyState } from "@/components/ui/EmptyState";
import { AlertIcon, BellIcon, ChartIcon } from "@/components/icons";
import { cn } from "@/lib/cn";
import { relativeTime } from "@/lib/time";

// Same lazy import the artifact card uses: a trend is a chart, and ECharts must
// not follow it into the entry bundle (PRD NFR-1).
const ChartCanvas = lazy(() =>
  import("@/components/artifacts/ChartCanvas").then((module) => ({
    default: module.ChartCanvas,
  })),
);

/**
 * The trend view (PRD FR-7.5): one numeric KPI across every snapshot.
 *
 * **A trend is an artifact.** Rather than a bespoke chart component, the series
 * is shaped into a `line_chart` artifact and handed to the same `ChartCanvas`
 * that draws everything else — so the axis formatting, the palette, the
 * tooltip, the theming and the dark-mode behaviour are the ones the rest of the
 * product already has. A second chart renderer would be a second set of bugs.
 *
 * Only numeric summary-card metrics are trendable, and the server decided that
 * (`app/reports/snapshots.py`). A KPI card is a claim about one number at one
 * moment, which is exactly a point on a line; a table row is not, because next
 * week's table may not have that row in it.
 */

function seriesArtifact(
  series: TrendSeries,
  locale: string,
): Artifact {
  const stamp = (iso: string) =>
    new Intl.DateTimeFormat(locale, { month: "short", day: "numeric" }).format(new Date(iso));
  return {
    index: 0,
    type: "line_chart",
    family: "series",
    title: series.label,
    from_query: null,
    x: "captured_at",
    y: series.label,
    series_by: "",
    size: "",
    items: [],
    text: "",
    data: null,
    notes: [],
    degraded_from: null,
    swap_options: [],
    chart: {
      categories: series.points.map((point) => stamp(point.captured_at)),
      series: [{ name: series.label, values: series.points.map((point) => point.value) }],
      points: [],
      y_categories: [],
      cells: [],
      x_label: "",
      y_label: series.label,
      category_format: "text",
      value_format: series.format,
      x_format: "number",
      size_format: "number",
      size_label: "",
      geo: "",
      geo_version: "",
    },
  };
}

function Delta({ series }: { series: TrendSeries }) {
  const formatter = useFormatter();
  const latest = series.points.at(-1);
  const first = series.points[0];
  if (!latest) return null;

  const change = latest.delta_percent;
  const direction = change === null ? "flat" : change > 0 ? "up" : change < 0 ? "down" : "flat";
  return (
    <div className="flex flex-wrap items-baseline gap-2">
      <span className="type-metric text-ink">
        {formatter.value(latest.value, series.format as ValueFormat)}
      </span>
      {change !== null ? (
        <span
          className={cn(
            "type-support",
            direction === "up" && "text-success",
            direction === "down" && "text-error",
            direction === "flat" && "text-ink-secondary",
          )}
        >
          {change > 0 ? "▲" : change < 0 ? "▼" : "—"} {formatter.value(Math.abs(change), "percent")}
          {" since the previous snapshot"}
        </span>
      ) : (
        // A change from zero has no percentage. Saying so beats an infinity.
        <span className="type-support text-ink-disabled">no comparable previous value</span>
      )}
      {first && series.points.length > 2 ? (
        <span className="type-caption text-ink-disabled normal-case">
          from {formatter.value(first.value, series.format as ValueFormat)} at the first snapshot
        </span>
      ) : null}
    </div>
  );
}

export function TrendPanel({
  trend,
  loading,
  locale,
  onAddAlert,
}: {
  trend: Trend | undefined;
  loading: boolean;
  locale: string;
  /** ALERT-2: a series is exactly a metric a rule can watch. Admin only. */
  onAddAlert?: (series: TrendSeries) => void;
}) {
  const series = useMemo(
    () => (trend?.series ?? []).filter((entry) => entry.points.length > 0),
    [trend],
  );
  const [selected, setSelected] = useState<string | null>(null);
  const active = series.find((entry) => entry.key === selected) ?? series[0];

  // Two tiles can each carry a KPI called "Orders". The key tells them apart
  // and the label does not, so a repeated label borrows its tile's title —
  // two identical chips are two chips a reader cannot choose between.
  const repeated = new Set(
    series
      .map((entry) => entry.label)
      .filter((label, index, all) => all.indexOf(label) !== index),
  );
  const chipLabel = (entry: TrendSeries) =>
    repeated.has(entry.label) && entry.tile_title
      ? `${entry.label} · ${entry.tile_title}`
      : entry.label;
  const artifact = useMemo(
    () => (active ? seriesArtifact(active, locale) : null),
    [active, locale],
  );

  if (loading) {
    return <div className="bg-muted rounded-container h-64 animate-pulse" />;
  }

  const snapshots = trend?.snapshots ?? 0;
  if (!series.length) {
    return (
      <EmptyState
        icon={<ChartIcon className="size-6" />}
        title={snapshots ? "No trendable metrics yet" : "No snapshots yet"}
        detail={
          snapshots
            ? "A trend charts numeric KPI cards across snapshots. This report's tiles are tables " +
              "and charts, which do not carry a single figure to follow over time."
            : "A snapshot captures this report's values with a timestamp. Take one now, or let a " +
              "schedule take them, and a line appears here."
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        {series.map((entry) => (
          <button
            key={entry.key}
            type="button"
            onClick={() => setSelected(entry.key)}
            className={cn(
              "type-support rounded-element motion-fast border px-2.5 py-1",
              entry.key === active?.key
                ? "border-accent bg-accent-subtle text-accent"
                : "border-border text-ink-secondary hover:bg-hover",
            )}
          >
            {chipLabel(entry)}
          </button>
        ))}
        <span className="flex-1" />
        <Chip tone={snapshots >= 3 ? "neutral" : "warning"}>
          {snapshots} snapshot{snapshots === 1 ? "" : "s"}
        </Chip>
      </div>

      {snapshots < 3 ? (
        <p className="type-support text-ink-secondary flex items-start gap-2">
          <AlertIcon className="text-warning mt-0.5 size-3.5 shrink-0" />
          <span>
            A trend is worth reading from three points onward. Take more snapshots, or wait for the
            schedule to.
          </span>
        </p>
      ) : null}

      {active ? (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-start gap-3">
            <div className="flex-1">
              <Delta series={active} />
            </div>
            {onAddAlert ? (
              <Button
                size="sm"
                icon={<BellIcon className="size-3.5" />}
                onClick={() => onAddAlert(active)}
              >
                Alert me
              </Button>
            ) : null}
          </div>
          {active.tile_title ? (
            <p className="type-caption text-ink-disabled normal-case">from “{active.tile_title}”</p>
          ) : null}
          <div className="bg-card border-border rounded-container border p-3">
            {artifact ? (
              <Suspense
                fallback={<div className="bg-muted rounded-element h-[280px] animate-pulse" />}
              >
                <ChartCanvas artifact={artifact} height={280} />
              </Suspense>
            ) : null}
          </div>
          <ol className="type-caption text-ink-secondary flex flex-col gap-1 normal-case">
            {[...active.points].reverse().slice(0, 6).map((point) => (
              <li key={point.snapshot_id} className="flex flex-wrap gap-2">
                <span className="text-ink-disabled">{relativeTime(point.captured_at, locale)}</span>
                <span className="text-ink">
                  <TrendValue value={point.value} format={active.format} />
                </span>
                {point.delta !== null ? (
                  <span className={point.delta >= 0 ? "text-success" : "text-error"}>
                    {point.delta >= 0 ? "+" : "−"}
                    <TrendValue value={Math.abs(point.delta)} format={active.format} />
                  </span>
                ) : null}
              </li>
            ))}
          </ol>
        </div>
      ) : null}
    </div>
  );
}

function TrendValue({ value, format }: { value: number; format: string }) {
  const formatter = useFormatter();
  return <>{formatter.value(value, format as ValueFormat)}</>;
}
